// Cached thumbnails as image sources offline, read where BL-2429 writes. On the lists (BL-2430) a
// row carries only its cover, from the thumbnail's file URL in the app (BL-2609) or a blob URL in a
// browser emulating it, when its thumbnail is on the device; an uncached Main
// item stays with its source blanked, so the row shows the no-photo placeholder (BUG-2044). The lot
// form (BL-2437) keeps every item. A photo saved offline gets its list thumbnail at Save (BUG-2081).
import { pickCoverUrl, RESIZED_IMAGE_WIDTH, type InventoryMediaItem } from '@auctogon/core/inventory';
import type { InventoryListItemDto } from '@auctogon/core-admin/schemas/responses/inventory-list';
import type { LotDetailPayload } from '@auctogon/core-admin/schemas/responses/lot-detail';

import { stagedFileIdOf, type OfflineMediaAdd } from '@auctogon/admin-isomorphic/isomorphic/offline/offline-attachments';

import type { OfflineMediaStaging, OfflineScope, OfflineStore, OfflineThumbnailSource } from '../../gateways/native-device';
import { offlineThumbnailKey } from '../../stores/offline-sync/offline-thumbnails';
import { offlineMediaStaging } from './offline-write-queue';
import { resizeImage } from '../resize-image';

/** The JPEG quality of a list thumbnail made on the device, the resizer's own (`q=75`, ADR 066 § 2). */
const LIST_THUMBNAIL_QUALITY = 0.75;

/**
 * Object URLs minted once per key and reused while they are recent: the lists re-read on every
 * filter change, and minting a URL per read would leak a blob each time. Past the cap the least
 * recently used URLs are revoked, so the pinned blobs stay bounded, but only once the read that
 * minted or reused them has handed them out: a read never revokes a URL it returns (BUG-2073).
 * Keys carry the scope, so a tenant or user switch never reuses another store's blob.
 */
interface ObjectUrlPool {
  /** The URL already minted for a key, marked most recently used; else null. */
  cached(key: string): string | null;
  /** The key's URL, minted now if it has none, marked most recently used. */
  urlFor(key: string, blob: Blob): string;
  /** Revoke the least recently used URLs past the cap, except the keys a read is handing out. */
  trim(keep: ReadonlySet<string>): void;
}

function createObjectUrlPool(cap: number): ObjectUrlPool {
  const urls = new Map<string, string>();
  const cached = (key: string): string | null => {
    const existing = urls.get(key);
    if (existing == null) return null;
    urls.delete(key);
    urls.set(key, existing);
    return existing;
  };
  return {
    cached,
    urlFor(key, blob) {
      const existing = cached(key);
      if (existing != null) return existing;
      const url = URL.createObjectURL(blob);
      urls.set(key, url);
      return url;
    },
    trim(keep) {
      for (const [key, url] of urls) {
        if (urls.size <= cap) return;
        if (keep.has(key)) continue;
        urls.delete(key);
        URL.revokeObjectURL(url);
      }
    },
  };
}

/**
 * The lists' cached covers. A list read mints one URL per row, so a page of 200 rows fits well
 * inside the cap with the previous pages' covers beside it.
 */
const listThumbnailUrls = createObjectUrlPool(500);

/**
 * The lot form's cached photos and posters, kept apart from the lists' so paging a list under an
 * open lot drawer can never evict the drawer's photos (BUG-2073).
 */
const formThumbnailUrls = createObjectUrlPool(200);

/**
 * Previews of files staged offline, kept apart so a list read can never evict a tile the open lot
 * form is showing (BUG-2073). Staging a file keeps only its own preview from the trim, so a form
 * showing more staged files than the cap can lose its oldest tiles. The cap is far above what one
 * lot holds. A staged file evicted here is read back and minted again the next time the form opens.
 */
const stagedPreviewUrls = createObjectUrlPool(200);

const stagedPreviewKey = (scope: OfflineScope, fileId: string) => `${scope.tenantId}:${scope.userId}:staged:${fileId}`;

/**
 * BL-2447: the preview URL of a file staged offline, minted once and kept in the staged previews'
 * pool the lot form's previews read, so a staged file has one URL and the pool's cap revokes it.
 *
 * @param input.scope - the staging's scope.
 * @param input.fileId - the staged file's id.
 * @param input.blob - its bytes.
 * @returns the object URL.
 * @sideEffect Mints an object URL the first time.
 */
export function stagedPreviewUrl(input: { scope: OfflineScope; fileId: string; blob: Blob }): string {
  const key = stagedPreviewKey(input.scope, input.fileId);
  const url = stagedPreviewUrls.urlFor(key, input.blob);
  stagedPreviewUrls.trim(new Set([key]));
  return url;
}

/**
 * Point each row's cover at its cached thumbnail. A list cell shows only the cover, so a row
 * carries only its cover offline: one URL per row, never one per photo (BUG-2073).
 *
 * @param input.store - The active scope's store.
 * @param input.rows - The list rows the offline query returned.
 * @returns The rows, each with its cover item (the Main one, else the first) alone. A cover with a
 *   cached thumbnail renders from a blob URL. One without stays with its source blanked, so
 *   `pickCoverUrl` gives the placeholder as it does online (BUG-2039, BUG-2044) instead of the
 *   next photo.
 * @sideEffect Mints an object URL per cover the first time it is shown, and revokes the least
 *   recently shown past the cap, never one this read returns.
 */
export async function withCachedThumbnails(input: {
  store: OfflineStore;
  rows: InventoryListItemDto[];
}): Promise<InventoryListItemDto[]> {
  // The item `pickCoverUrl` reads: the Main one, else the first.
  const covers = input.rows.map((row) => (row.media ? (row.media.find((m) => m.isCover) ?? row.media[0]) : undefined));
  // A thumbnail lives under its lot's auction; a lot outside one has none (BUG-2081).
  const wanted = input.rows.flatMap((row, i) => {
    const cover = covers[i];
    return cover && row.auctionId ? [{ key: offlineThumbnailKey({ lotId: row.lotId, mediaId: cover.id }), auctionId: row.auctionId }] : [];
  });
  const sources = wanted.length === 0 ? [] : await input.store.thumbnails.sources(wanted);
  const byKey = new Map(wanted.map((item, i) => [item.key, sources[i]] as const));
  const scopeKey = `${input.store.scope.tenantId}:${input.store.scope.userId}`;
  const shown = new Set<string>();
  const rows = input.rows.map((row, i) => {
    const cover = covers[i];
    if (!row.media) return row;
    if (!cover) return { ...row, media: [] };
    const key = offlineThumbnailKey({ lotId: row.lotId, mediaId: cover.id });
    const source = byKey.get(key);
    if (!source) return { ...row, media: [withoutSource(cover)] };
    const url = sourceUrl({ pool: listThumbnailUrls, poolKey: `${scopeKey}:${key}`, source, shown });
    return { ...row, media: [{ ...cover, url, thumbnail: url }] };
  });
  listThumbnailUrls.trim(shown);
  return rows;
}

/**
 * A cached thumbnail's image URL: a file's own URL as it is (nothing to mint or revoke), or a
 * blob's object URL from the pool, marked shown so this read's trim keeps it (BUG-2073).
 */
function sourceUrl(input: { pool: ObjectUrlPool; poolKey: string; source: OfflineThumbnailSource; shown: Set<string> }): string {
  const { pool, poolKey, source, shown } = input;
  if (source.kind === 'url') return source.url;
  shown.add(poolKey);
  return pool.urlFor(poolKey, source.blob);
}

/** A media item with no image source: nothing to fetch offline, and no still to show. */
function withoutSource(item: InventoryMediaItem): InventoryMediaItem {
  const { thumbnail: _remotePoster, ...rest } = item;
  return { ...rest, url: '' };
}

/**
 * BL-2437 (operator decision Q3): point a cached lot detail's photos and video posters at their
 * cached thumbnails, for the offline lot form. Unlike the lists, every media item is KEPT (an
 * uncached one keeps its remote url and shows the image's own placeholder): the form's media
 * array is a later save's per-item baseline (BL-2348), and a dropped item would read as a delete.
 * The cover follows the one cover rule (`pickCoverUrl`, BUG-2039).
 *
 * BL-2447 (AC 6): a photo picked offline shows from its staged file, and a video picked offline
 * from its staged poster (the video itself is never read back just to show a tile).
 *
 * @param input.store - The active scope's store.
 * @param input.detail - The cached lot detail.
 * @returns The detail with cached media sources.
 * @sideEffect Mints an object URL per thumbnail or staged file the first time it is shown, and
 *   revokes the least recently shown past each pool's cap, never one this read returns.
 */
export async function withCachedDetailThumbnails(input: {
  store: OfflineStore;
  detail: LotDetailPayload;
}): Promise<LotDetailPayload> {
  const { detail, store } = input;
  if (detail.media.length === 0) return detail;
  const keys = detail.media.map((item) => offlineThumbnailKey({ lotId: detail.id, mediaId: item.id }));
  const { auctionId } = detail;
  const sources = auctionId ? await store.thumbnails.sources(keys.map((key) => ({ key, auctionId }))) : keys.map(() => null);
  const staging = offlineMediaStaging(store.scope);
  const scopeKey = `${store.scope.tenantId}:${store.scope.userId}`;
  const { scope } = store;
  const shown = new Set<string>();
  const shownStaged = new Set<string>();
  const media = await Promise.all(
    detail.media.map(async (item, i) => {
      if (stagedFileIdOf(item.storagePath) != null) return withStagedPreview({ item, staging, scope, shown: shownStaged });
      const source = sources[i];
      if (!source) return item;
      const url = sourceUrl({ pool: formThumbnailUrls, poolKey: `${scopeKey}:${keys[i]}`, source, shown });
      // A video keeps its own url (the file); only its poster is a cached still.
      return item.type === 'video' ? { ...item, thumbnail: url } : { ...item, url };
    }),
  );
  formThumbnailUrls.trim(shown);
  stagedPreviewUrls.trim(shownStaged);
  return { ...detail, media, image: pickCoverUrl(media) };
}

/** A staged photo's own bytes, or a staged video's poster, as an object URL; else unchanged. */
async function withStagedPreview(input: {
  item: InventoryMediaItem;
  staging: OfflineMediaStaging | null;
  scope: OfflineScope;
  /** The staged preview keys this read hands out, which its trim must keep. */
  shown: Set<string>;
}): Promise<InventoryMediaItem> {
  const { item, staging, scope } = input;
  const fileId = stagedFileIdOf(item.type === 'video' ? item.posterStoragePath : item.storagePath);
  if (!staging || fileId == null) return item;
  const key = stagedPreviewKey(scope, fileId);
  input.shown.add(key);
  // A staged file never changes, so a preview already shown is reused without reading it again.
  const cached = stagedPreviewUrls.cached(key);
  if (cached != null) return item.type === 'video' ? { ...item, thumbnail: cached } : { ...item, url: cached };
  const staged = await staging.read(fileId).catch((err: unknown) => {
    // A preview that can't be read shows the tile's placeholder; the file still uploads at replay.
    // eslint-disable-next-line no-console -- client module; createLogger is server-only (ALS).
    console.warn('[offline-thumbnails] could not read a staged file', err instanceof Error ? err : new Error(String(err)));
    return null;
  });
  if (!staged) return item;
  const url = stagedPreviewUrls.urlFor(key, staged.blob);
  return item.type === 'video' ? { ...item, thumbnail: url } : { ...item, url };
}

/**
 * BUG-2081: give each photo a Save adds offline a list-sized thumbnail now, under the key the sync's
 * thumbnails use, so the Lots tab and Inventory show it as a cover at once. A video's comes from its
 * staged poster; its own file is never read. Once the add lands under the same media id, the sync
 * finds the thumbnail already held and keeps it, so the cover never blinks to the placeholder.
 * Best effort: a photo whose thumbnail can't be made shows the placeholder until it syncs, and the
 * Save it rides on is never failed by it.
 *
 * @param input.store - the active scope's store.
 * @param input.lotId - the lot the photos were saved on.
 * @param input.auctionId - its auction; a lot outside one has no thumbnail slot, so nothing is written.
 * @param input.adds - the Save's photo and video adds.
 * @returns once every thumbnail that could be made is stored.
 * @sideEffect Reads staged files back and writes thumbnails to the device store.
 */
export async function cacheStagedListThumbnails(input: {
  store: OfflineStore;
  lotId: string;
  auctionId: string | null;
  adds: readonly OfflineMediaAdd[];
}): Promise<void> {
  const { store, lotId, auctionId } = input;
  const staging = offlineMediaStaging(store.scope);
  if (auctionId == null || !staging) return;
  // One photo at a time: each decode holds a full-size bitmap, and a Save can add dozens of photos.
  const made: Array<{ key: string; auctionId: string; blob: Blob } | null> = [];
  for (const item of input.adds) made.push(await listThumbnailOf({ staging, lotId, auctionId, item }));
  const stored = made.filter((thumb) => thumb != null);
  if (stored.length === 0) return;
  // The Save has already committed: a store that refuses the thumbnails (a full disk) leaves the
  // placeholder, never a failed Save whose retry would queue the same photos twice.
  try {
    const sizes = await store.thumbnails.putMany(stored);
    await addToThumbnailAggregate({ store, auctionId, sizes });
  } catch (err) {
    // eslint-disable-next-line no-console -- client module; createLogger is server-only (ALS).
    console.warn('[offline-thumbnails] could not store the list thumbnails of a Save', err instanceof Error ? err : new Error(String(err)));
  }
}

/**
 * Count the thumbnails a Save made in the auction's aggregate (BL-2609), so the Storage sheet's
 * size takes them in before the next sync measures the auction again.
 */
async function addToThumbnailAggregate(input: { store: OfflineStore; auctionId: string; sizes: { bytes: number }[] }): Promise<void> {
  const { store, auctionId, sizes } = input;
  const record = await store.records.get({ collection: 'auctions', key: auctionId });
  if (record == null || sizes.length === 0) return;
  const added = sizes.reduce((sum, size) => sum + size.bytes, 0);
  const thumbnails = record.thumbnails ?? { count: 0, bytes: 0 };
  await store.records.put({
    collection: 'auctions',
    key: auctionId,
    value: { ...record, bytes: record.bytes + added, thumbnails: { count: thumbnails.count + sizes.length, bytes: thumbnails.bytes + added } },
  });
}

/** One staged photo's (or a staged video poster's) list thumbnail, or null when it can't be made. */
async function listThumbnailOf(input: {
  staging: OfflineMediaStaging;
  lotId: string;
  auctionId: string;
  item: OfflineMediaAdd;
}): Promise<{ key: string; auctionId: string; blob: Blob } | null> {
  const { staging, lotId, auctionId, item } = input;
  const fileId = stagedFileIdOf(item.type === 'video' ? item.posterStoragePath : item.storagePath);
  if (fileId == null) return null;
  try {
    const staged = await staging.read(fileId);
    if (!staged) return null;
    const small = await resizeImage({ image: staged.blob, maxEdge: RESIZED_IMAGE_WIDTH.listThumbnail, quality: LIST_THUMBNAIL_QUALITY });
    // An image already list-sized is its own thumbnail.
    return { key: offlineThumbnailKey({ lotId, mediaId: item.id }), auctionId, blob: small ?? staged.blob };
  } catch (err) {
    // eslint-disable-next-line no-console -- client module; createLogger is server-only (ALS).
    console.warn('[offline-thumbnails] could not make a list thumbnail for a staged photo', err instanceof Error ? err : new Error(String(err)));
    return null;
  }
}
